"use client";

import { IconHelp, IconMail, IconShare3 } from "@tabler/icons-react";
import { useTheme } from "next-themes";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { Button } from "@/components/ui/Button";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { TUTORIAL_ID } from "@/features/onboarding/appInfo";
import { useShareModal } from "@/features/share/useShareUrl";
import { useModal } from "@/hooks/useModal";
import type { AppLocale } from "@/i18n/routing";
import { cn } from "@/lib/cn";
import { LegendMenu } from "./LegendMenu";

/**
 * The toolbar in the map's top corner.
 *
 * Owns which dropdown is open, rather than each control tracking its own: they
 * overlay the map from the same row, so two open at once would collide. A click
 * anywhere outside closes whichever is open.
 */
export function ToolBar({ locales }: { locales: AppLocale[] }) {
  const t = useTranslations();
  const configs = useProjectConfigs();
  const { resolvedTheme } = useTheme();
  const { open: openShareModal } = useShareModal();
  const { open: openTutorial } = useModal(TUTORIAL_ID);

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openDropdown) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!toolbarRef.current?.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [openDropdown]);

  const toggleDropdown = (name: string) =>
    setOpenDropdown((current) => (current === name ? null : name));

  const { contactEmail, navbarBrandLink } = configs;
  // The mark is dark-on-light, so it needs its light variant on a dark theme.
  const brandLogo =
    resolvedTheme === "dark"
      ? "/images/vectuel_logo_white.svg"
      : "/images/vectuel_logo.svg";

  return (
    <div
      ref={toolbarRef}
      className={cn(
        "pointer-events-auto z-10 flex h-14 min-w-0 shrink-0 items-center gap-1",
        "rounded-l-full bg-white p-2 pl-3 dark:bg-dark-color"
      )}
    >
      <LegendMenu
        isOpen={openDropdown === "legend"}
        onToggle={() => toggleDropdown("legend")}
      />

      {configs.tutorialEnabled && (
        <Button variant="ghost" size="xs" onClick={openTutorial}>
          <IconHelp size={16} className="text-neutral-500" aria-hidden />
          <span className="text-sm font-normal">{t("help")}</span>
        </Button>
      )}

      <Button variant="ghost" size="xs" onClick={openShareModal}>
        <IconShare3 size={16} className="text-neutral-500" aria-hidden />
        <span className="text-sm font-normal">{t("share")}</span>
      </Button>

      {contactEmail && (
        <Button variant="ghost" size="xs" asChild>
          <a href={`mailto:${contactEmail}`}>
            <IconMail size={16} className="text-neutral-500" aria-hidden />
            <span className="text-sm font-normal">{t("contact")}</span>
          </a>
        </Button>
      )}

      <LocaleSwitcher locales={locales} />

      <a
        href={navbarBrandLink || "https://vectuel.com"}
        target="_blank"
        rel="noopener noreferrer"
        className="ml-1 shrink-0"
        title={t("error.poweredBy")}
      >
        <Image
          src={brandLogo}
          alt="Vectuel"
          width={60}
          height={24}
          className="max-w-[60px]"
        />
      </a>
    </div>
  );
}
